import * as React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import ProjectBlogButton from '../components/ProjectBlogButton';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';

const ROWS = 6;
const COLS = 7;

type Board = number[][];
type ColHeights = number[];
type Coord = [number, number];

interface StatsRow {
  move: number;
  col: number;
  timeElapsed: number;
  nodesThisTurn: number;
  totalNodes: number;
  depth: number;
}

function createBoard(): Board {
  return Array.from({ length: ROWS }, () => Array(COLS).fill(0));
}

function isValidMove(colHeights: ColHeights, col: number): boolean {
  return colHeights[col] < ROWS;
}

function makeMove(board: Board, colHeights: ColHeights, col: number, player: number): void {
  for (let row = ROWS - 1; row >= 0; row--) {
    if (board[row][col] === 0) {
      board[row][col] = player;
      colHeights[col]++;
      break;
    }
  }
}

function undoMove(board: Board, colHeights: ColHeights, col: number): void {
  for (let row = 0; row < ROWS; row++) {
    if (board[row][col] !== 0) {
      board[row][col] = 0;
      colHeights[col]--;
      break;
    }
  }
}

function checkWin(board: Board, player: number): Coord[] | null {
  for (let r = 0; r < ROWS; r++) {
    for (let c = 0; c <= COLS - 4; c++) {
      if (board[r][c] === player && board[r][c+1] === player && board[r][c+2] === player && board[r][c+3] === player)
        return [[r,c],[r,c+1],[r,c+2],[r,c+3]];
    }
  }
  for (let c = 0; c < COLS; c++) {
    for (let r = 0; r <= ROWS - 4; r++) {
      if (board[r][c] === player && board[r+1][c] === player && board[r+2][c] === player && board[r+3][c] === player)
        return [[r,c],[r+1,c],[r+2,c],[r+3,c]];
    }
  }
  for (let r = 3; r < ROWS; r++) {
    for (let c = 0; c <= COLS - 4; c++) {
      if (board[r][c] === player && board[r-1][c+1] === player && board[r-2][c+2] === player && board[r-3][c+3] === player)
        return [[r,c],[r-1,c+1],[r-2,c+2],[r-3,c+3]];
    }
  }
  for (let r = 3; r < ROWS; r++) {
    for (let c = 3; c < COLS; c++) {
      if (board[r][c] === player && board[r-1][c-1] === player && board[r-2][c-2] === player && board[r-3][c-3] === player)
        return [[r,c],[r-1,c-1],[r-2,c-2],[r-3,c-3]];
    }
  }
  return null;
}

function evaluateBoard(board: Board): number {
  if (checkWin(board, 1)) return -1000;
  if (checkWin(board, -1)) return 1000;
  let score = 0;
  for (let r = 0; r < ROWS; r++) {
    score += (3 - Math.abs(3 - board[r].indexOf(-1))) * 0.5;
    score -= (3 - Math.abs(3 - board[r].indexOf(1))) * 0.5;
  }
  return score;
}

function minimax(
  board: Board,
  colHeights: ColHeights,
  depth: number,
  isMaximizing: boolean,
  alpha: number,
  beta: number,
  maxDepth: number,
  counter: { nodes: number },
): number {
  if (depth === maxDepth || checkWin(board, 1) || checkWin(board, -1)) {
    counter.nodes++;
    return evaluateBoard(board);
  }
  if (isMaximizing) {
    let best = -Infinity;
    for (let col = 0; col < COLS; col++) {
      if (isValidMove(colHeights, col)) {
        makeMove(board, colHeights, col, -1);
        const score = minimax(board, colHeights, depth + 1, false, alpha, beta, maxDepth, counter);
        undoMove(board, colHeights, col);
        best = Math.max(best, score);
        alpha = Math.max(alpha, score);
        if (beta <= alpha) break;
      }
    }
    return best;
  } else {
    let best = Infinity;
    for (let col = 0; col < COLS; col++) {
      if (isValidMove(colHeights, col)) {
        makeMove(board, colHeights, col, 1);
        const score = minimax(board, colHeights, depth + 1, true, alpha, beta, maxDepth, counter);
        undoMove(board, colHeights, col);
        best = Math.min(best, score);
        beta = Math.min(beta, score);
        if (beta <= alpha) break;
      }
    }
    return best;
  }
}

function computeBestMove(
  board: Board,
  colHeights: ColHeights,
  maxDepth: number,
  totalNodesSoFar: number,
  moveNumber: number,
): { col: number; statsRow: StatsRow } {
  const start = performance.now();
  const counter = { nodes: 0 };
  let bestScore = -Infinity;
  let bestCol = 0;
  for (let col = 0; col < COLS; col++) {
    if (isValidMove(colHeights, col)) {
      makeMove(board, colHeights, col, -1);
      const score = minimax(board, colHeights, 0, false, -Infinity, Infinity, maxDepth, counter);
      undoMove(board, colHeights, col);
      if (score > bestScore) { bestScore = score; bestCol = col; }
    }
  }
  return {
    col: bestCol,
    statsRow: {
      move: moveNumber,
      col: bestCol,
      timeElapsed: performance.now() - start,
      nodesThisTurn: counter.nodes,
      totalNodes: totalNodesSoFar + counter.nodes,
      depth: maxDepth,
    },
  };
}

const NAVY = '#001f7a';

export default function C4SolverPage() {
  const [boardState, setBoardState] = React.useState<Board>(createBoard);
  const [winnerText, setWinnerText] = React.useState('');
  const [winningCoords, setWinningCoords] = React.useState<Coord[]>([]);
  const [stats, setStats] = React.useState<StatsRow[]>([]);
  const [maxDepth, setMaxDepth] = React.useState(4);
  const [gameOver, setGameOver] = React.useState(false);
  const [aiThinking, setAiThinking] = React.useState(false);

  const boardRef = React.useRef<Board>(createBoard());
  const colHeightsRef = React.useRef<ColHeights>(Array(COLS).fill(0));
  const totalNodesRef = React.useRef(0);
  const moveCountRef = React.useRef(0);
  const statsEndRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    statsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [stats]);

  function handleTileClick(col: number) {
    if (gameOver || aiThinking) return;
    if (!isValidMove(colHeightsRef.current, col)) return;

    makeMove(boardRef.current, colHeightsRef.current, col, 1);
    setBoardState(boardRef.current.map(r => [...r]));

    const playerWin = checkWin(boardRef.current, 1);
    if (playerWin) {
      setWinnerText('Player (Red) Wins!');
      setWinningCoords(playerWin);
      setGameOver(true);
      return;
    }

    setAiThinking(true);
    const depth = maxDepth;
    setTimeout(() => {
      const { col: aiCol, statsRow } = computeBestMove(
        boardRef.current,
        colHeightsRef.current,
        depth,
        totalNodesRef.current,
        moveCountRef.current + 1,
      );
      totalNodesRef.current += statsRow.nodesThisTurn;
      moveCountRef.current += 1;

      makeMove(boardRef.current, colHeightsRef.current, aiCol, -1);
      setBoardState(boardRef.current.map(r => [...r]));
      setStats(prev => [...prev, statsRow]);

      const aiWin = checkWin(boardRef.current, -1);
      if (aiWin) {
        setWinnerText('AI (Yellow) Wins!');
        setWinningCoords(aiWin);
        setGameOver(true);
      }
      setAiThinking(false);
    }, 30);
  }

  function handleReset() {
    boardRef.current = createBoard();
    colHeightsRef.current = Array(COLS).fill(0);
    totalNodesRef.current = 0;
    moveCountRef.current = 0;
    setBoardState(createBoard());
    setWinnerText('');
    setWinningCoords([]);
    setStats([]);
    setGameOver(false);
    setAiThinking(false);
  }

  const isWinning = (r: number, c: number) =>
    winningCoords.some(([wr, wc]) => wr === r && wc === c);

  return (
    <Box sx={{ px: { xs: 2, sm: 4 }, py: { xs: 2, sm: 4 }, maxWidth: 1200, mx: 'auto' }}>
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
        C4AI: Minimax &amp; Alpha-Beta Pruning
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        An interactive Connect 4 AI using the minimax algorithm with alpha-beta pruning.
        You play red; click a column to drop a piece.
      </Typography>
      <Box sx={{ mb: 3 }}>
        <ProjectBlogButton slug="c4solver" />
      </Box>

      {winnerText && (
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 2, textAlign: 'center', color: 'error.main' }}>
          {winnerText}
        </Typography>
      )}

      <Box sx={{ display: 'flex', gap: 2, mb: 2, minHeight: { md: '60vh' }, overflowX: 'auto' }}>
        {/* Board */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: `repeat(${COLS}, 72px)`,
            gridTemplateRows: `repeat(${ROWS}, 72px)`,
            gap: '4px',
            bgcolor: NAVY,
            border: `5px solid ${NAVY}`,
            borderRadius: 1,
            p: '4px',
            alignSelf: 'start',
            flexShrink: 0,
          }}
        >
          {boardState.map((row, r) =>
            row.map((cell, c) => {
              const winning = isWinning(r, c);
              return (
                <Box
                  key={`${r}-${c}`}
                  onClick={() => handleTileClick(c)}
                  sx={{
                    height: 72,
                    width: 72,
                    bgcolor: 'white',
                    borderRadius: '50%',
                    border: winning ? '5px solid gold' : `5px solid ${NAVY}`,
                    boxSizing: 'border-box',
                    overflow: 'hidden',
                    position: 'relative',
                    cursor: gameOver || aiThinking ? 'default' : 'pointer',
                    transition: 'border-color 0.15s, transform 0.1s',
                    animation: winning ? 'c4glow 1s infinite alternate' : undefined,
                    '@keyframes c4glow': {
                      from: { boxShadow: '0 0 5px gold' },
                      to: { boxShadow: '0 0 20px gold, 0 0 5px gold' },
                    },
                    '&:hover': !(gameOver || aiThinking) ? {
                      borderColor: 'rgba(100,180,255,0.9)',
                      transform: 'scale(1.06)',
                    } : undefined,
                  }}
                >
                  {cell !== 0 && (
                    <Box
                      sx={{
                        position: 'absolute',
                        inset: 0,
                        bgcolor: cell === 1 ? '#d32f2f' : '#fdd835',
                      }}
                    />
                  )}
                </Box>
              );
            })
          )}
        </Box>

        {/* Side panel */}
        <Box
          sx={{
            flex: 1,
            border: `5px solid ${NAVY}`,
            borderRadius: 1,
            bgcolor: 'grey.100',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            minWidth: 300,
          }}
        >
          <Box sx={{ px: 1.5, py: 1, borderBottom: `2px solid ${NAVY}`, bgcolor: NAVY }}>
            <Typography variant="caption" sx={{ color: 'white', fontWeight: 600 }}>
              {aiThinking ? 'AI thinking...' : 'AI Move Log'}
            </Typography>
          </Box>
          <Box sx={{ flex: 1, overflowY: 'auto' }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {['Move', 'Column', 'Time (ms)', 'Nodes (Turn)', 'Nodes (Total)', 'Depth'].map(h => (
                    <TableCell key={h} sx={{ bgcolor: `${NAVY} !important`, color: 'white', fontWeight: 700, whiteSpace: 'nowrap' }}>
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {stats.map((row, i) => (
                  <TableRow key={i} hover>
                    <TableCell sx={{ color: '#111' }}>{row.move}</TableCell>
                    <TableCell sx={{ color: '#111' }}>{row.col}</TableCell>
                    <TableCell sx={{ color: '#111' }}>{row.timeElapsed.toFixed(2)}</TableCell>
                    <TableCell sx={{ color: '#111' }}>{row.nodesThisTurn.toLocaleString()}</TableCell>
                    <TableCell sx={{ color: '#111' }}>{row.totalNodes.toLocaleString()}</TableCell>
                    <TableCell sx={{ color: '#111' }}>{row.depth}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <div ref={statsEndRef} />
          </Box>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <Button variant="contained" onClick={handleReset} disabled={aiThinking}>
          Reset
        </Button>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2">AI Search Depth:</Typography>
          <TextField
            type="number"
            value={maxDepth}
            onChange={(e) => {
              let val = parseInt(e.target.value);
              if (isNaN(val) || val < 1) val = 1;
              if (val > 7) val = 7;
              setMaxDepth(val);
            }}
            slotProps={{ htmlInput: { min: 1, max: 7 } }}
            size="small"
            sx={{ width: 80 }}
            disabled={aiThinking}
          />
        </Box>
        <Typography variant="caption" color="text.secondary">
          Depth 5+ may take several seconds per move.
        </Typography>
      </Box>
    </Box>
  );
}
